import React, { useEffect, useState } from 'react'
import IntersectionVisible from 'components/IntersectionVisible'
import { getWistiaPlaylist } from './util'
import loadExternalLibrary from 'util/loadExternalLibrary'
import './wistia-embed.scss'

// Currently used by VideoStep and PersonalyQuiz ResultSlide
// The wistia video will fill its container, so make sure the container has a width specified
const WistiaEmbed = ({
  videoIDs,
  handleVideoPlayed,
  handleVideoWatched,
  onLoaded,
  hasClickedNext,
}) => {
  let [videoInfo, setVideoInfo] = useState([])
  let [activeVideoID, setActiveVideoID] = useState(videoIDs[0])
  let [loadingVideo, setLoadingVideo] = useState(false)
  const [initialEmbedOptions, setInitialEmbedOptions] = useState('')

  //Classes also includes the options passed to the component, so we have to do some weird witchcraft here.
  //More info here: https://wistia.com/support/developers/embed-options
  const embedDivClasses = optionsObject => {
    // console.log("setting video embed options")
    // let wistiaOptions = embedOptions || {}
    // wistiaOptions.videoFoam = true
    let classes = []
    for (const prop in optionsObject) {
      classes.push(`${prop}=${optionsObject[prop]}`)
    }
    return (
      classes.join(' ') +
      ` wistia--video--embed wistia_embed wistia_async_${videoIDs[0]} `
    )
  }
  //br-2159: turn off autoplay for now
  // const checkAndAutoplayVideoIfSpecified = () => {
  //   try {
  //    if (window._wVidz[0].data.media.embed_options.autoPlay) {
  //        window._wVidz[0].play()
  //      }
  //    } catch (e) {}
  //  }
  // const handleIsOnScreen = isIntersecting => {
  //   if (isIntersecting) {
  //      checkAndAutoplayVideoIfSpecified()
  //    }
  //  }

  //Info about Wistia js api here: https://wistia.com/support/developers/player-api#secondswatched
  const createVideoBindings = () => {
    window._wq = window._wq || []
    window._wVidz = window._wVidz || [] // our own internal map. used to pause all videos.
    window._wq.push({
      id: '_all',
      onReady: function (video) {
        window._wVidz.push(video)
        try {
          video.bind('play', function () {
            setLoadingVideo(false)
            setActiveVideoID(video.data.media.hashedId)
            handleVideoPlayed &&
              handleVideoPlayed(
                video.data.media.hashedId,
                video.data.media.name,
              )
          })
        } catch (e) {
          console.log(`wistia binding error: ${e}`)
        }

        try {
          // 85%: video.bind("crosstime", (video.data.media.duration * .85), function() {
          video.bind('crosstime', 3, function () {
            handleVideoWatched &&
              handleVideoWatched(
                video.data.media.hashedId,
                video.data.media.name,
              )
          })
        } catch (e) {
          console.log(`wistia binding error: ${e}`)
        }
      },
    })
  }

  //on desktop view, make sure playlist maxes out height same as video pane, and add a cute scrollbar.
  const makePlaylistPretty = () => {
    if (videoIDs.length > 1) {
      //If has playlist
      let playlistContainer = document.querySelector(
        '.wistia--video--playlist-positioner',
      )
      let playlistHeight = document
        .querySelector('.wistia--video--playlist-list')
        .getBoundingClientRect().height
      let height = document
        .querySelector('.wistia--video--embed')
        .getBoundingClientRect().height
      if (playlistHeight > height) {
        playlistContainer.classList.add('is--truncated')
        playlistContainer.style.maxHeight = height + 'px'
      }
    }
  }

  const onWistiaLoaded = function () {
    return new Promise(function (resolve, reject) {
      window._wq = window._wq || []

      // Set up timeout as backup
      const timeout = setTimeout(() => {
        reject('timeout exceeded')
      }, 10000) // 10 seconds timeout

      // Use Wistia's official ready notification
      window._wq.push({
        id: videoIDs[0],
        onReady: function(video) {
          clearTimeout(timeout)
          resolve(video)
        }
      })
    })
  }

  const pauseAllVids = () => {
    if (window._wVidz) {
      window._wVidz.forEach(v => {
        v.pause()
      })
    }
  }

  //make sure the options we set in wistia get passed/bound to window stuff.
  const bindVideoOptions = videos => {
    try {
      let initialEmbedObject = videos[0].media.embed_options
      initialEmbedObject.autoPlay = false
      console.log('setting video embed options')
      let embedOptsString = embedDivClasses(initialEmbedObject)
      setInitialEmbedOptions(embedOptsString)
      videos.forEach(function (video) {
        window._wq = window._wq || []
        window._wq.push({
          id: video.embedID,
          options: video.media.embed_options,
          onReady: function () {},
        })
      })
    } catch (e) {
      console.log(`error embedding wistia vids ${e}`)
    }
  }

  const requiresMobileCSS = () => {
    let ret = false
    try {
      let activeVid = videoInfo.filter(function (item) {
        if (item.embedID === activeVideoID) {
          return item
        }
      })[0]
      ret = activeVid.aspectRatioIsRealTall
    } catch (e) {}
    return ret
  }

  useEffect(() => {
    requiresMobileCSS()
    async function startVideo() {
      createVideoBindings()
      await loadExternalLibrary({
        url: 'https://fast.wistia.com/assets/external/E-v1.js',
        name: 'wistia',
      })

      //Use ajax to get wistia metadata.
      await getWistiaPlaylist(videoIDs).then(data => {
        let dataWithAutoPlayRemoved = data.map(item => {
          item.media.embed_options.autoPlay = false
          return item
        })

        bindVideoOptions(dataWithAutoPlayRemoved)
        setVideoInfo(dataWithAutoPlayRemoved)
      })

      await onWistiaLoaded().then(
        result => {
          onLoaded && onLoaded()

          makePlaylistPretty()
        },
        error => {
          console.log(error)
        },
      )
    }
    startVideo()
  }, [])

  useEffect(() => {
    pauseAllVids()
  }, [hasClickedNext])

  //allows for loading animation to cover up potential video resizing needing to occur
  const onActiveVideoChange = vid => {
    setLoadingVideo(true)
  }
  return (
    <>
      <div
        className={`wistia--video ${loadingVideo ? 'video-loading' : ''} ${
          requiresMobileCSS() ? `mobile-vid` : `desktop-vid`
        } ${videoIDs.length > 1 ? 'has--playlist' : 'no--playlist'}`}
      >
        {initialEmbedOptions !== '' && (
          <div className="wistia--video--container">
            <div className="wistia--video--wrapper">
              <div className={embedDivClasses()}>&nbsp;</div>
            </div>
          </div>
        )}
        {videoIDs.length >
          1 /* "videoIDs.length > 1" is shorthand for "this has a playlist" */ && (
          <div className="wistia--video--playlist">
            <div className="wistia--video--playlist-positioner">
              <ul className="wistia--video--playlist-list">
                {videoInfo.map((video, index) => {
                  return (
                    <li
                      key={index}
                      className={
                        video.embedID === activeVideoID
                          ? 'is--active'
                          : 'is--inactive'
                      }
                      onClick={() => onActiveVideoChange(video)}
                    >
                      <a
                        className={'wistia--video--playlist-box'}
                        href={`#wistia_${video.embedID}`}
                      >
                        <div className={'wistia--video--thumbnail'}>
                          <img alt={video.media.name} src={video.thumbURL} />
                        </div>
                        <div className={'wistia--video--thumbnail--title'}>
                          {video.media.name}
                        </div>
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default WistiaEmbed
